import { createContext, useContext, ReactNode, useMemo, useRef } from 'react'

import { FerretSearchProps } from '@verbit-ai/ferret-web'

import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { getCollectionId } from 'src/utils/env'
import { useResponsive } from 'src/hooks/useResponsive'

type FerretSearchWrapperContextValue = {
    shouldShowFerretSearch: boolean
    ferretSearchRef: FerretSearchProps['ref']
}

const FerretSearchWrapperContext = createContext<FerretSearchWrapperContextValue | undefined>(undefined)

type FerretSearchWrapperProviderProps = {
    children: ReactNode
}

const FerretSearchWrapperProvider = ({ children }: FerretSearchWrapperProviderProps) => {
    const isFerretSearchEnabled = useFeatureFlag('ferret_search')
    const collectionId = getCollectionId()
    const { isMobilePhone } = useResponsive()

    const shouldShowFerretSearch = !!(isFerretSearchEnabled && collectionId && !isMobilePhone)
    const ferretSearchRef: FerretSearchProps['ref'] = useRef(null)

    return (
        <FerretSearchWrapperContext.Provider
            value={useMemo(
                () => ({
                    shouldShowFerretSearch,
                    ferretSearchRef,
                }),
                [shouldShowFerretSearch, ferretSearchRef],
            )}>
            {children}
        </FerretSearchWrapperContext.Provider>
    )
}

const useFerretSearch = () => {
    const context = useContext(FerretSearchWrapperContext)
    if (!context) {
        throw new Error('useFerretSearch must be used within a FerretSearchWrapperProvider')
    }

    return context
}

export { FerretSearchWrapperProvider, useFerretSearch }
